<script setup>
    // Vue
    import { useRouter, useRoute } from 'vue-router';
    import { inject, onMounted, onUnmounted, ref, reactive, onBeforeUnmount } from 'vue';
    import { getCurrentInstance } from 'vue';

    // Component
    import BaseInput from '@/components/BaseInputComponent.vue';
    import MainHeader from '@/components/HeaderComponent.vue';
    import PasswordInput from '@/components/PasswordInputComponent.vue';
    import Tooltip from '@/components/TooltipComponent.vue';

    // Constats
    import { constant } from '../../../public/config/Const';

    // Modal
    import { LoginFormModel } from '@/models/login/loginFormModel';
    import { signinMfaFormInfo } from '@/models/signinModel';
    import { setupPasswordApplyInfo } from '@/models/setupModel';

    // Store
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useHeaderStore } from '@/stores/header/headerStore';
    import { useHomeStore } from '@/stores/home/homeStore';
    import { useLanguageStore } from '@/stores/common/languageStore';
    import { useLoginStore } from '@/stores/login/loginStore';
    import { useNewsDetailStore } from '@/stores/modal/newsDetailStore';
    import { useSigninMfaStore } from '@/stores/signin/signinMfaStore';
    import { useSetupAgreeStore } from '@/stores/setup/setupAgreeStore';
    import { useSetupPasswordStore } from '@/stores/setup/setupPasswordStore';
    import { useTouStore } from '@/stores/common/touStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Utis
    import utils from '@/commonProject/helpers/utils';

    // Else
    import i18n from '@/common/i18n';

    // ----------------------------
    // 定数
    // ----------------------------
    const app = getCurrentInstance();

    // 遷移情報取得
    const router = useRouter();
    const route = useRoute();

    // ログイン画面のプログラム内で、画面の表示モードを制御する。
    // 0：ログイン画面
    // 1：MFA暗証コード入力画面
    // 2：パスワード再設定入力画面
    // 3：パスワード再設定完了画面
    let mode = ref(0);

    // ----------------------------
    // ログイン情報
    // ----------------------------
    const loginForm = ref(new LoginFormModel());
    const signinMfaForm = reactive(new signinMfaFormInfo());
    const setupPasswordApplyForm = reactive(new setupPasswordApplyInfo());

    const languageStore = useLanguageStore();
    const loginStore = useLoginStore();

    const commonStore = useCommonStore();

    const headerStore = useHeaderStore();

    const homeStore = useHomeStore();
    const newsDetailStore = useNewsDetailStore();

    const signinMfaStore = useSigninMfaStore();
    const setupAgreeStore = useSetupAgreeStore();
    const setupPasswordStore = useSetupPasswordStore();
    const touStore = useTouStore();

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    const ssoLoginUrl = ref('');

    // Cognito認証が必須のAPI
    const API_URL_S = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // Cognito認証が不要なAPI
    const API_URL_P = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_P;

    // SSOでログインした場合は、通常ログインの入力欄やログインボタンを非活性にするフラグ。
    const isSsoLoggedinAndNormalLoginInputDisabled = ref(false);
    
    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    // 描画されたヘッダーの高さを取得する
    const headerWrap = ref(null);
    // ハンバーガーメニューの状態監視用
    let overlayObserver = null;
    let syncChatbotVisibility = null;

    async function reset() {
        // Storeを初期化する。
        homeStore.resetAll();

        // 画面モードをログイン画面モードに初期化する。
        mode.value = 0;

        // SSOでログインしているかを判定するフラグを更新する。
        commonStore.isSsoLoggedin = false;

        // 初期化する。
        loginForm.value.companyid = '';
        loginForm.value.userid = '';
        loginForm.value.password = '';
        signinMfaForm.authenticationCode = '';
        setupPasswordApplyForm.password = '';
        setupPasswordApplyForm.repassword = '';

        // SSOログイン時のAPIでメンテナンスモードに判定されたか判定する。
        if (!utils.isNullEmpty(route.name) && route.name === 'login3') {
            // エラー応答のステータスを初期化する。
            // 初期値は正常。
            let status = '0';

            // エラー応答のステータスが存在するか判定する。
            if (!utils.isNullEmpty(route.query.status)) {
                // エラー応答を格納する。
                status = String(route.query.status);
            }

            // エラーに応答に応じて判定する。
            switch (status) {
                case '0':
                default:
                    // 正常の場合。
                    // 何もしない。
                    break;
            }

            // 記録されている可能性があるローカルストレージのベンダーIDを維持する変数を初期化する。
            let vendorId = null;

            // ローカルストレージにベンダーIDが格納されているか判定する。
            if (!utils.isNullEmpty(loginStore.getVendorId)) {
                // 記録されている可能性があるローカルストレージのベンダーIDを一時的に保持する。
                vendorId = loginStore.getVendorId;
            }

            // 初期化する。
            commonStore.resetAllStorage();

            // Storeを初期化する。
            loginStore.reset();

            // ベンダーIDをloginStoreに再設定する。
            if (!utils.isNullEmpty(vendorId)) {
                // ベンダーIDを復元する。
                loginStore.setVendorId(vendorId);
            }

            // メンテナンス画面へ遷移する。
            router.push({ name: 'maintenance' });
            return;
        }

        // ログインモードに応じて動作を制御する。
        if (!utils.isNullEmpty(route.query.code)) {
            // SSOログインを実行する場合のルート

            // 記録されている可能性があるローカルストレージのベンダーIDを維持する変数を初期化する。
            let vendorId = null;

            // ローカルストレージにベンダーIDが格納されているか判定する。
            if (!utils.isNullEmpty(loginStore.getVendorId)) {
                // 記録されている可能性があるローカルストレージのベンダーIDを一時的に保持する。
                vendorId = loginStore.getVendorId;
            }

            // 初期化する。
            commonStore.resetAllStorage();

            // Storeを初期化する。
            loginStore.reset();

            // ベンダーIDをloginStoreに再設定する。
            if (!utils.isNullEmpty(vendorId)) {
                // ベンダーIDを復元する。
                loginStore.setVendorId(vendorId);
            }

            // SSOログイン管理フラグを有効にする。
            isSsoLoggedinAndNormalLoginInputDisabled.value = true;

            let ssoCalledUrl = location.href.split('?')[0];

            if (!utils.isNullEmpty(route.name) && route.name === 'login2') {
                ssoCalledUrl = location.protocol + '//' + location.host + '/cp/link2';
            }

            // 取得したTokenを使って、CognitoのSSO認証を試みる。
            const response1 = await loginStore.cognitoTokenCallAction(route.query.code, ssoCalledUrl);

            // SSO認証の結果を判定。
            if (!response1.success) {
                messageDialog('error', i18n.global.t('login.message.message10'));
                return;
            }

            // ブラウザに保存するSSO認証のトークン情報のKey名を準備する。
            const hash = await loginStore.sha256();
            const keyName = 'CognitoIdentityServiceProvider' + '.' + constant.CLIENT_ID_2 + '.' + hash + '.';
            const accessToken = 'accessToken';
            const idToken = 'idToken';
            const refreshToken = 'refreshToken';
            const clockDrift = 'clockDrift';
            const lastAuthUser = 'CognitoIdentityServiceProvider' + '.' + constant.CLIENT_ID_2 + '.' + 'LastAuthUser';

            // ブラウザにSSO認証したトークン情報を保存する。
            localStorage.setItem('mngUser', hash);
            localStorage.setItem(keyName + accessToken, response1.responseBody.access_token);
            localStorage.setItem(keyName + idToken, response1.responseBody.id_token);
            localStorage.setItem(keyName + refreshToken, response1.responseBody.refresh_token);
            localStorage.setItem(keyName + clockDrift, '0');
            localStorage.setItem(lastAuthUser, hash);

            // SSOでログインしているかを判定するフラグを更新する。
            commonStore.isSsoLoggedin = true;

            // SSOログインを初回で完了したので、Cognitoセッションのリフレッシュトークンの更新処理を実行する。
            const token = await loginStore.isTokenValidCheckByCallApi();

            if (token) {
                // ユーザーログイン完了APIをコールする。クエリパラメーターの条件は設定しない。
                const response2 = await loginStore.postUserLoginDateUpdateAction(API_URL_S, null);

                if (response2.success) {
                    // ユーザー情報取得APIをコールする。クエリパラメーターの条件は設定しない。
                    const response3 = await loginStore.getLoginUserInfoAction(API_URL_S, null);

                    if (response3.success) {
                        // このユーザーのベンダーIDを保存する。
                        loginStore.setVendorId(response3.responseBody.VendorId);

                        // メンテナンス状態取得APIをコールする。
                        const vendorMaintenanceResults = await getVendorMaintenanceMode();

                        // メンテナンスモードでは無いか判定する。
                        if (vendorMaintenanceResults) {
                            // URLにニュースIDが存在するか判定する。
                            if (!utils.isNullEmpty(route.query.news)) {
                                // URLにニュースIDが存在する場合。

                                // お知らせ詳細取得APIをコールする。
                                const newsQueryParam = {
                                    Lang: languageStore.getLanguageValue,
                                    NoticeType: '2', // 個別お知らせモード。
                                    NoticeHistPk: route.query.news, // URLからニュースIDを取得する。
                                };

                                // お知らせ詳細取得APIをコールする。
                                const newsResponse = await newsDetailStore.getNewsDetailAction(API_URL_S, newsQueryParam);

                                // 表示するニュースする情報をStoreに保管する。
                                homeStore.KeepNewsDetail.openNewDetailModalInHomeFlag = true;
                                homeStore.KeepNewsDetail.NoticeType = '2';
                                homeStore.KeepNewsDetail.NoticeBulletinPk = null; // 使用しない。
                                homeStore.KeepNewsDetail.NoticeHistPk = route.query.news;

                                // ニュースが取得できた場合。
                                // Cognito認証済み。
                                if (newsResponse.success) {
                                    // ユーザー状態確認(ログイン)APIをコールする。
                                    await getUserDetailStatus();
                                } else {
                                    messageDialog('error', newsResponse.errorMessage);
                                    if (newsResponse.redirect) {
                                        location.href = newsResponse.redirectUrl;
                                    }
                                }
                            } else {
                                // URLにニュースIDが存在しない通常ルートのSSO認証の場合。

                                // ユーザー状態確認(ログイン)APIをコールする。
                                await getUserDetailStatus();
                            }
                        }
                    } else {
                        messageDialog('error', response3.errorMessage);
                        if (response3.redirect) {
                            location.href = response3.redirectUrl;
                        }
                    }
                } else {
                    messageDialog('error', response2.errorMessage);
                    if (response2.redirect) {
                        location.href = response2.redirectUrl;
                    }
                }
            } else {
                await loginStore.cognitoLogoutAction();
            }

            // この時点で、SSOログインに何かしら失敗したということで、SSOログイン管理フラグを無効に戻す。
            isSsoLoggedinAndNormalLoginInputDisabled.value = false;
        } else {
            // SSOログインでは無い場合。

            // 何かしらのログインでエラーが発生し、クエリパラメーター上に「error」情報が存在する場合。
            if (!utils.isNullEmpty(route.query.error)) {
                // ログインでエラーが発生したメッセージを表示する。
                messageDialog('error', i18n.global.t('login.message.message11'));
            }

            // ベンダーIDが取得できない場合は、システムエラーを表示して以降の処理を実施しない。
            if (utils.isNullEmpty(route.query.vid)) {
                if (utils.isNullEmpty(loginStore.getVendorId)) {
                    // Storeを初期化する。
                    loginStore.reset();

                    messageDialog('error', i18n.global.t('common.message.apiResponseSystemError'));
                    return;
                }
            } else {
                // Storeを初期化する。
                loginStore.reset();

                // ベンダーIDをloginStoreに代入する。
                loginStore.setVendorId(route.query.vid);
            }

            // タフネス情報を保管するStoreを初期化する。
            touStore.reset();

            // メンテナンス状態取得APIをコールする。
            const vendorMaintenanceResults = await getVendorMaintenanceMode();

            // メンテナンスモードでは無いか判定する。
            if (vendorMaintenanceResults) {
                // URLにニュースIDが存在するか判定する。
                if (!utils.isNullEmpty(route.query.news)) {
                    // お知らせ詳細取得APIをコールする。
                    const newsQueryParam = {
                        Lang: languageStore.getLanguageValue,
                        NoticeType: '2', // 個別お知らせモード。
                        NoticeHistPk: route.query.news, // URLからニュースIDを取得する。
                    };

                    // お知らせ詳細取得APIをコールする。
                    const newsResponse = await newsDetailStore.getNewsDetailAction(API_URL_S, newsQueryParam);

                    // 表示するニュースする情報をStoreに保管する。
                    homeStore.KeepNewsDetail.openNewDetailModalInHomeFlag = true;
                    homeStore.KeepNewsDetail.NoticeType = '2';
                    homeStore.KeepNewsDetail.NoticeBulletinPk = null; // 使用しない。
                    homeStore.KeepNewsDetail.NoticeHistPk = route.query.news;

                    // ニュースが取得できた場合。
                    // Cognito認証済み。
                    if (newsResponse.success) {
                        // ユーザー情報取得APIをコールする。クエリパラメーターの条件は設定しない。
                        const response4 = await loginStore.getLoginUserInfoAction(API_URL_S, null);

                        if (response4.success) {
                            // 入力フォームを初期化する。
                            loginForm.value.companyid = '';
                            loginForm.value.userid = '';
                            signinMfaForm.authenticationCode = '';
                            setupPasswordApplyForm.password = '';
                            setupPasswordApplyForm.repassword = '';

                            // ユーザー状態確認(ログイン)APIをコールする。
                            await getUserDetailStatus();
                        } else {
                            messageDialog('error', response4.errorMessage);
                            if (response4.redirect) {
                                location.href = response4.redirectUrl;
                            }
                            return;
                        }
                    } else {
                        messageDialog('error', newsResponse.errorMessage);
                        if (newsResponse.redirect) {
                            location.href = newsResponse.redirectUrl;
                        }
                        return;
                    }
                }
            }
        }
    }

    /**
     * メンテナンス状態取得APIをコールして、本ベンダーがログイン不可状態かを判定する。
     */
    async function getVendorMaintenanceMode() {
        // クエリパラメーターの準備。
        const queryParam1 = {
            VendorId: loginStore.getVendorId,
        };

        // メンテナンス状態取得APIをコールする。
        const response1 = await loginStore.getMaintenanceModeAction(API_URL_P, queryParam1);

        if (response1.success) {
            // メンテナンスモードが有効の場合は、メンテナンス画面に遷移させる。
            if (loginStore.getMaintenanceMode) {
                // メンテナンス画面へ遷移する。
                router.push({ name: 'maintenance' });
                return false;
            } else {
                // ベンダー情報取得APIをコールする。クエリパラメーターは同一条件なので引き継ぐ。
                const response2 = await loginStore.getVendorInfoAction(API_URL_P, queryParam1);

                if (response2.success) {
                    // タフネスログインURL。ログアウト時の遷移先として使用する。
                    touStore.touLoginUrl = response2.responseBody.TouLoginUrl;

                    // エラー画面でタフネスに設定を行わないといけない場合の画面のURL。
                    touStore.touProfileUrl = response2.responseBody.TouProfileUrl;

                    // SSOログイン先のURLを、「ストレスチェックのユーザーIDでログイン」ボタンに設定する。
                    ssoLoginUrl.value = response2.responseBody.SsoLoginUrl;

                    return true;
                } else {
                    // ページが見つかりません画面へ遷移する。
                    router.push({ name: 'pageNotFound' });
                    if (response1.redirect) {
                        location.href = response1.redirectUrl;
                    }
                    return false;
                }
            }
        } else {
            // エラーメッセージを表示して以降の処理を実施しない。
            messageDialog('error', response1.errorMessage);
            if (response1.redirect) {
                location.href = response1.redirectUrl;
            }
            return false;
        }
    }

    /**
     * ログイン処理
     */
    async function login() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // 初期化する。
        commonStore.resetAllStorage();

        // URLにニュースIDが存在するか判定する。
        if (!utils.isNullEmpty(route.query.news)) {
            // 表示するニュースする情報をStoreに保管する。
            homeStore.KeepNewsDetail.openNewDetailModalInHomeFlag = true;
            homeStore.KeepNewsDetail.NoticeType = '2';
            homeStore.KeepNewsDetail.NoticeBulletinPk = null; // 使用しない。
            homeStore.KeepNewsDetail.NoticeHistPk = route.query.news;
        }

        // Cognitoユーザーネームを生成する。
        const cognitoUserName = 'cng1' + '_' + loginStore.vendorId + '_' + loginForm.value.companyid + '_' + loginForm.value.userid;

        // Cognito認証処理を実行する。
        const response = await loginStore.cognitoLoginAction(cognitoUserName, loginForm.value.password);

        // 現在ログインしたCognitoユーザー名をブラウザのローカルストレージに保存する。
        localStorage.setItem('mngUser', cognitoUserName);

        switch (response.status) {
            case 'success': {
                // 通常のログイン成功

                // ユーザーログイン完了APIをコールする。クエリパラメーターの条件は設定しない。
                const response3 = await loginStore.postUserLoginDateUpdateAction(API_URL_S, null);

                if (response3.success) {
                    // ユーザー情報取得APIをコールする。クエリパラメーターの条件は設定しない。
                    const response4 = await loginStore.getLoginUserInfoAction(API_URL_S, null);

                    if (response4.success) {
                        // 入力フォームを初期化する。
                        loginForm.value.companyid = '';
                        loginForm.value.userid = '';
                        signinMfaForm.authenticationCode = '';
                        setupPasswordApplyForm.password = '';
                        setupPasswordApplyForm.repassword = '';

                        // ユーザー状態確認(ログイン)APIをコールする。
                        await getUserDetailStatus();
                    } else {
                        messageDialog('error', response4.errorMessage);
                        if (response4.redirect) {
                            location.href = response4.redirectUrl;
                        }
                        return;
                    }
                } else {
                    messageDialog('error', response3.errorMessage);
                    if (response3.redirect) {
                        location.href = response3.redirectUrl;
                    }
                    return;
                }

                break;
            }
            case 'mfa-required':
                // MFAコード入力画面に切り替える。
                mode.value = 1;
                break;
            default:
                // 画面遷移せず、ログイン画面にとどまり、エラーを表示する。
                messageDialog('error', i18n.global.t('common.message.cognitoError'));
                break;
        }
    }

    /**
     * ユーザー状態確認(ログイン)APIをコールして、ログインしているユーザーのログイン後の画面遷移を制御する。
     */
    async function getUserDetailStatus() {
        // ユーザー状態確認(ログイン)APIをコールして、ユーザーの各種状態を取得する。クエリパラメーターの条件は設定しない。
        const response4 = await loginStore.getUserStatusByLoginAction(API_URL_S, null);

        // 正常応答の場合。
        if (response4.success) {
            // 有効な契約が存在するか判定する。
            // 有効化契約が存在しない場合は、エラーメッセージを表示して、画面に留まる。
            if (response4.responseBody.ContractAssigned) {
                // ログインしたユーザーのログイン後の画面の切り替えを制御する。
                if (response4.responseBody.TouStatusIsInvalid) {
                    // タフネスログインでタフネス状態が不正の場合。
                    router.push({ name: 'error' });
                } else if (response4.responseBody.UserPwdNeed2Update) {
                    // パスワード再設定入力画面モードに切り替える。
                    mode.value = 2;
                } else if (response4.responseBody.CompanyPwdNeed2Check) {
                    // 企業パスワード入力画面へ遷移する。
                    router.push({ name: 'setupCompany' });
                } else if (response4.responseBody.RegulationNeed2Agree) {
                    // 利用規定同意要否を判定する。

                    // 規定PKを保持する。
                    setupAgreeStore.setRegulationPk(response4.responseBody.RegulationPk);

                    // 利用規定同意画面へ遷移する。
                    router.push({ name: 'setupAgree' });
                } else if (response4.responseBody.UserInfoNeed2Update) {
                    // ユーザー情報更新要否を判定する。

                    // ユーザー情報入力画面へ遷移する。
                    router.push({ name: 'setupUser' });
                } else if (response4.responseBody.ParentAgreeNeed2Check) {
                    // 親権者同意確認要否を判定する。

                    // 親権者同意画面へ遷移する。
                    router.push({ name: 'setupParent' });
                } else {
                    // 上記のいずれも不要、もしくは、すでに同意済み等で設定が不要な場合。

                    // ホーム画面へ遷移する。
                    router.push({ name: 'home' });
                }
            } else {
                // 有効な契約が存在しない場合はエラーにする。
                messageDialog('error', i18n.global.t('common.message.isContractAssignError'));

                // ログアウト処理を実行する。
                const logoutUrl = await headerStore.logoutAction();

                // ログアウトの遷移先に応じて移動する。
                if (logoutUrl.href) {
                    location.href = logoutUrl.url;
                } else {
                    // ログイン画面自体なので、ログイン画面モードに戻す。
                    await reset();
                    //router.push({ name: logoutUrl.url, query: logoutUrl.query });
                }
            }
        } else {
            messageDialog('error', response4.errorMessage);
            if (response4.redirect) {
                location.href = response4.redirectUrl;
            }
            return;
        }
    }

    /**
     * MFA暗唱コード入力
     */
    async function checkAuthenticationCode() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }

        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // CognitoのMFA認証を実行する。
        const response = await signinMfaStore.cognitoAuthByAuthenticationCodeAction(signinMfaForm.authenticationCode);

        // MFA認証が失敗したかを判定する。
        if (response.status !== 'success') {
            messageDialog('error', i18n.global.t('common.message.cognitoError'));
            return;
        } else {
            // MFA認証が成功した場合は、ログインしたユーザーの状態に応じて画面遷移を制御する。

            // ユーザーログイン完了APIをコールする。クエリパラメーターの条件は設定しない。
            const response1 = await loginStore.postUserLoginDateUpdateAction(API_URL_S, null);

            if (response1.success) {
                // ユーザー情報取得APIをコールする。クエリパラメーターの条件は設定しない。
                const response2 = await loginStore.getLoginUserInfoAction(API_URL_S, null);

                if (response2.success) {
                    // 入力フォームを初期化する。
                    loginForm.value.companyid = '';
                    loginForm.value.userid = '';
                    signinMfaForm.authenticationCode = '';
                    setupPasswordApplyForm.password = '';
                    setupPasswordApplyForm.repassword = '';

                    // ユーザー状態確認(ログイン)APIをコールする。
                    await getUserDetailStatus();
                } else {
                    messageDialog('error', response2.errorMessage);
                    if (response2.redirect) {
                        location.href = response2.redirectUrl;
                    }
                    return;
                }
            } else {
                messageDialog('error', response1.errorMessage);
                if (response1.redirect) {
                    location.href = response1.redirectUrl;
                }
                return;
            }
        }
    }

    /**
     * パスワード再設定入力
     */
    async function updatePasswordReset() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }

        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // Cognitoのパスワードを更新する。
        const response = await setupPasswordStore.cognitoUpadatePasswordByFirstLoginAction(loginForm.value.password, setupPasswordApplyForm.password);

        // Cognitoのパスワードが正常に更新できたか判定する。
        switch (response.status) {
            case 'change-password-success':
                {
                    // ユーザーパスワード更新完了APIをコールする。クエリパラメーターの条件は設定しない。
                    const response1 = await loginStore.postUserLoginPasswordUpdateedDateUpdateAction(API_URL_S, null);

                    if (response1.success) {
                        // 初期化する。
                        loginForm.value.vendorid = '';
                        loginForm.value.userid = '';
                        loginForm.value.password = '';
                        signinMfaForm.authenticationCode = '';
                        setupPasswordApplyForm.password = '';
                        setupPasswordApplyForm.repassword = '';

                        // パスワード再設定完了画面モードに切り替える
                        mode.value = 3;
                    } else {
                        messageDialog('error', response1.errorMessage);
                        if (response1.redirect) {
                            location.href = response1.redirectUrl;
                        }
                        return;
                    }
                }
                break;
            case 'change-password-failed':
                // 通常のパスワードエラー。
                messageDialog('error', i18n.global.t('common.message.cognitoError'));
                break;
            case 'no-password-reuse':
                // パスワードの使いまわしエラー。
                messageDialog('error', i18n.global.t('common.message.noPasswordReuse'));
                break;
            case 'NO SESSION':
                messageDialog('error', i18n.global.t('common.message.errorSession'));
                break;
            default:
                // 何もしない。
                break;
        }
    }

    /**
     *
     */
    function ssoLogin() {
        if (!utils.isNullEmpty(ssoLoginUrl.value)) {
            location.href = ssoLoginUrl.value;
        }
    }

    /**
     * ホーム画面か、もしくは別画面に遷移する。
     * パスワードの再設定が完了した後の、パスワード再設定完了画面での動作。
     */
    async function goToHome() {
        // ユーザー状態確認(ログイン)APIをコールして、ログインしているユーザーのログイン後の画面遷移を制御する。
        await getUserDetailStatus();
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // 画面モードに応じてバリデーションチェックを行う対象を制御する。
        switch (mode.value) {
            case 0:
                {
                    // ログイン画面のバリデーションチェックを実行する。

                    // 企業IDの入力判定。
                    if (utils.isNullEmpty(loginForm.value.companyid)) {
                        message += i18n.global.t('login.message.message1') + '\n';
                    } else {
                        // 企業IDが入力されている場合。
                        if (utils.checkLengthOfCompanyId(loginForm.value.companyid)) {
                            message += i18n.global.t('login.message.message2') + '\n';
                        }
                        if (!utils.isMatchPatternAlphabetNumber(loginForm.value.companyid)) {
                            message += i18n.global.t('login.message.message3') + '\n';
                        }
                    }

                    // ユーザーIDの入力判定。
                    if (utils.isNullEmpty(loginForm.value.userid)) {
                        message += i18n.global.t('login.message.message4') + '\n';
                    } else {
                        // ユーザーIDが入力されている場合。
                        if (utils.checkLengthOfUserId(loginForm.value.userid)) {
                            message += i18n.global.t('login.message.message5') + '\n';
                        }
                        if (!utils.isMatchPatternLowerAlphabetNumber(loginForm.value.userid)) {
                            message += i18n.global.t('login.message.message6') + '\n';
                        }
                    }

                    // パスワードの入力判定。
                    if (utils.isNullEmpty(loginForm.value.password)) {
                        message += i18n.global.t('login.message.message7') + '\n';
                    }
                }
                break;
            case 1:
                {
                    // MFA暗証コード入力画面のバリデーションチェックを実行する。

                    // 認証コードの入力チェック。
                    if (utils.isNullEmpty(signinMfaForm.authenticationCode)) {
                        message += i18n.global.t('signinMfa.message.message1') + '\n';
                    }
                }
                break;
            case 2:
                {
                    // パスワード再設定入力画面のバリデーションチェックを実行する。

                    // 新しいパスワード
                    if (utils.isNullEmpty(setupPasswordApplyForm.password)) {
                        message += i18n.global.t('setupPasswordApply.message.message1') + '\n';
                    } else {
                        if (utils.checkLengthOfPassword(setupPasswordApplyForm.password)) {
                            message += i18n.global.t('setupPasswordApply.message.message2') + '\n';
                        }
                        if (!utils.isMatchPatternAlphabetNumberSymbol(setupPasswordApplyForm.password)) {
                            message += i18n.global.t('setupPasswordApply.message.message3') + '\n';
                        }
                    }
                    // 新しいパスワードの再入力
                    if (utils.isNullEmpty(setupPasswordApplyForm.repassword)) {
                        message += i18n.global.t('setupPasswordApply.message.message4') + '\n';
                    } else {
                        if (utils.checkLengthOfPassword(setupPasswordApplyForm.repassword)) {
                            message += i18n.global.t('setupPasswordApply.message.message5') + '\n';
                        }
                        if (!utils.isMatchPatternAlphabetNumberSymbol(setupPasswordApplyForm.repassword)) {
                            message += i18n.global.t('setupPasswordApply.message.message6') + '\n';
                        }
                    }
                    // パスワードの一致
                    if (!utils.isStringMatch(setupPasswordApplyForm.password, setupPasswordApplyForm.repassword)) {
                        message += i18n.global.t('common.message.emptyPasswordUnmatch1') + '\n';
                    }
                }
                break;
            default:
                // 何もしない。
                break;
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    function jumpToPage(routerPageName) {
        // 各画面へ遷移する。
        // signinUseridApply：ユーザーID通知画面へ
        // signinResetApply：パスワード再設定画面へ
        // signupApply：ユーザー情報設定画面へ
        router.push({ name: routerPageName });
    }

    /**ログインチャットボットからタフネス画面に遷移する際に入力内容を初期化する処理-start- */
    function resetInputs() {
        loginForm.value.companyid = '';
        loginForm.value.userid = '';
        loginForm.value.password = '';
    }

    function handleBeforeUnload() {
        resetInputs();
    }

    onBeforeUnmount(() => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
    })
    /**ログインチャットボットからタフネス画面に遷移する際に入力内容を初期化する処理-end- */

    onMounted(async () => {
        // onMountedでStoreを読み込ませるための処理。
        commonStore.API_URL = API_URL_S;
        loginStore.firstFlag = true;

        //ログインチャットボットからタフネス画面に遷移する際に入力内容を初期化する処理
        window.addEventListener('beforeunload', handleBeforeUnload);

        const el = document.getElementById('chatbotContainer');
        if (el) {
            el.classList.add('is-login');
        }

        /**ハンバーガーメニューのopen/closeに応じてチャットボットリンクを表示・非表示する処理-start- */
        const overlay = document.getElementById('smartPhoneOverlay');

        if (el && overlay) {
            // チャットボットリンクの表示・非表示を切り替える処理
            syncChatbotVisibility = () => {
                const isOpen = overlay.classList.contains('open');

                if (isOpen) {
                    // openの場合はチャットボットリンクを非表示
                    el.style.display = 'none';
                } else {
                    // closeの場合はチャットボットリンクを表示
                    el.style.display = '';
                }
            }

            // 初回判定
            syncChatbotVisibility();

            // open/close のclass変化があった場合、チャットボットリンクの表示・非表示を切り替える
            overlayObserver = new MutationObserver(() => {
                syncChatbotVisibility();
            })

            // class属性の属性変化(open/close)を監視
            overlayObserver.observe(overlay, {
                attributes: true,
                attributeFilter: ['class']
            })

            // 画面幅変更時にも再判定
            window.addEventListener('resize', syncChatbotVisibility);
        }
        /**ハンバーガーメニューのopen/closeに応じてチャットボットリンクを表示・非表示する処理-end- */

        // 画面を初期化する。
        await reset();
    });

    onUnmounted(() => {
        const el = document.getElementById('chatbotContainer')
        if (el) {
            el.classList.remove('is-login')
        }
    })
</script>

<style></style>

<template>
    <template v-if="mode === 0">
        <!--ログイン画面-->
        <section id="main" class="bg login">
            <div ref="headerWrap">
                <main-header />
            </div>
            <div id="contents">
                <div id="loginArea" class="doubleBlock">
                    <section id="loginLeftBlock">
                        <h3>{{ $t('login.parts.pageTitle1') }}</h3>
                        <div class="form">
                            <dl>
                                <dt>
                                    <span>{{ $t('common.parts.companyId') }}</span>
                                </dt>
                                <dd>
                                    <base-input v-model:input="loginForm.companyid" type="text" :disabled="isSsoLoggedinAndNormalLoginInputDisabled" />
                                </dd>
                            </dl>
                            <dl>
                                <dt>
                                    <span>{{ $t('common.parts.userId') }}</span>
                                </dt>
                                <dd>
                                    <base-input v-model:input="loginForm.userid" type="text" :disabled="isSsoLoggedinAndNormalLoginInputDisabled" />
                                </dd>
                            </dl>
                            <dl>
                                <dt>
                                    <span>{{ $t('common.parts.password') }}</span>
                                </dt>
                                <dd>
                                    <PasswordInput v-model:input="loginForm.password" :isPropsPasswordInputDisabled="isSsoLoggedinAndNormalLoginInputDisabled" />
                                </dd>
                            </dl>
                        </div>
                        <div class="btnArea">
                            <button type="button" id="loginBtn" class="btnPrimary" @click="login" :disabled="isSsoLoggedinAndNormalLoginInputDisabled || disabledStore.isDisabled">
                                {{ $t('login.parts.button1') }}
                            </button>
                        </div>
                        <ul class="loginSupport">
                            <li>
                                <!--企業ID・ユーザーIDをお忘れの方-->
                                <button type="button" class="link" @click="jumpToPage('signinUseridApply')" :disabled="isSsoLoggedinAndNormalLoginInputDisabled">{{ $t('login.parts.linkButton1') }}</button>
                            </li>
                            <li>
                                <!--パスワードをお忘れの方-->
                                <button type="button" class="link" @click="jumpToPage('signinResetApply')" :disabled="isSsoLoggedinAndNormalLoginInputDisabled">{{ $t('login.parts.linkButton2') }}</button>
                            </li>
                            <li>
                                <!--ユーザーIDをお持ちでない場合-->
                                <button type="button" class="link" @click="jumpToPage('signupApply')" :disabled="isSsoLoggedinAndNormalLoginInputDisabled">{{ $t('login.parts.linkButton3') }}</button>
                            </li>
                        </ul>
                    </section>
                    <section id="loginRightBlock">
                        <article>
                            <h3>{{ $t('login.parts.pageTitle2') }}</h3>
                            <ul class="loginSupport">
                                <li>
                                    <!--ストレスチェックのユーザーIDでログイン-->
                                    <button type="button" class="link" @click="ssoLogin" :disabled="isSsoLoggedinAndNormalLoginInputDisabled">{{ $t('login.parts.linkButton4') }}</button>
                                </li>
                            </ul>
                        </article>
                    </section>
                </div>
            </div>
        </section>
        <teleport to="#chatbotContainer">
            <div v-if="loginStore.getVendorId === '1'" class="fixed-bottom-right" ref="loginchat">
                <a :href="'https://app.chatplus.jp/chat/visitor/ef8e9f49_5?t=btn'" target="_blank" style="align-content: center;" class="btnPrimary">
                    {{ $t('login.parts.linkButton5') }}
                </a>
            </div>
        </teleport>
    </template>
    <template v-else-if="mode === 1">
        <!--MFA暗唱コード入力画面-->
        <section id="main" class="bg login extra">
            <MainHeader />
            <div id="contents">
                <h2>{{ $t('signinMfa.parts.pageTitle1') }}</h2>
                <h3>{{ $t('signinMfa.parts.pageInformation1') }}</h3>
                <h3>{{ $t('signinMfa.parts.pageInformation2') }}</h3>
                <h3 style="margin-bottom: 20px">{{ $t('signinMfa.parts.pageInformation3') }}</h3>
                <section class="form" style="margin-bottom: 40px">
                    <dl>
                        <dt>
                            <span>{{ $t('signinMfa.parts.dl1') }}</span>
                        </dt>
                        <dd>
                            <BaseInput v-model:input="signinMfaForm.authenticationCode" type="text" />
                        </dd>
                    </dl>
                </section>
                <ul class="btnArea">
                    <li>
                        <button type="button" class="btnPrimary" @click="checkAuthenticationCode" :disabled="disabledStore.isDisabled">
                            {{ $t('signinMfa.parts.button1') }}
                        </button>
                    </li>
                </ul>
            </div>
        </section>
    </template>
    <template v-else-if="mode === 2">
        <!--パスワード再設定入力画面-->
        <section id="main" class="bg login extra">
            <MainHeader />
            <div id="contents" class="medium">
                <h2>{{ $t('setupPasswordApply.parts.pageTitle1') }}</h2>
                <section class="form">
                    <dl>
                        <dt style="padding-top: 10px">
                            <label>{{ $t('setupPasswordApply.parts.dl1') }}</label>
                            <div class="statusInfoBox resetPassword">
                                <Tooltip :classWidth="240">{{ $t('setupPasswordApply.parts.blowoutInformation1') }}</Tooltip>
                            </div>
                        </dt>
                        <dd>
                            <PasswordInput v-model:input="setupPasswordApplyForm.password" type="text" />
                        </dd>
                    </dl>
                    <dl>
                        <dt>
                            <label>{{ $t('setupPasswordApply.parts.dl2') }}</label>
                        </dt>
                        <dd>
                            <PasswordInput v-model:input="setupPasswordApplyForm.repassword" type="text" />
                        </dd>
                    </dl>
                </section>
                <ul class="btnArea">
                    <li>
                        <button type="button" class="btnPrimary" @click="updatePasswordReset" :disabled="disabledStore.isDisabled">{{ $t('setupPasswordApply.parts.button1') }}</button>
                    </li>
                </ul>
            </div>
        </section>
    </template>
    <template v-else-if="mode === 3">
        <!--パスワード再設定完了画面-->
        <section id="main" class="bg login extra">
            <MainHeader />
            <div id="contents" class="small">
                <h2>{{ $t('setupPasswordComplete.parts.pageTitle1') }}</h2>
                <h3 style="text-align: center; padding-top: 30px">{{ $t('setupPasswordComplete.parts.blowoutInformation1') }}</h3>
                <ul class="btnArea">
                    <li>
                        <button type="button" class="btnPrimary" @click="goToHome">{{ $t('setupPasswordComplete.parts.button1') }}</button>
                    </li>
                </ul>
            </div>
        </section>
    </template>
</template>